import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { documentAPI } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import { FileText, Download, Receipt, Building2, Calendar, DollarSign, X, ScrollText } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const TenantDocuments = () => {
  const { t } = useI18n();
  const [leaseDocs, setLeaseDocs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptModal, setReceiptModal] = useState(null);
  const [statementLoading, setStatementLoading] = useState(null);

  // Generate last 6 months for statement download
  const getStatementMonths = () => {
    const months = [];
    const now = new Date();
    for (let i = 1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      });
    }
    return months;
  };

  const handleDownloadStatement = async (month) => {
    setStatementLoading(month);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const res = await api.get(`/scheduler/preview-statement/${user.id}?month=${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement-${month}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No data for this month or statement unavailable');
    } finally {
      setStatementLoading(null);
    }
  };

  useEffect(() => { loadDocuments(); }, []);

  const loadDocuments = async () => {
    try {
      const res = await documentAPI.myDocuments();
      setLeaseDocs(res.data.lease_documents || []);
      setReceipts(res.data.payment_receipts || []);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await documentAPI.download(doc.path);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handleViewReceipt = async (receipt) => {
    try {
      const res = await documentAPI.getReceipt(receipt.payment_id);
      setReceiptModal(res.data.receipt);
    } catch {
      toast.error('Failed to load receipt');
    }
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateReceiptHTML(receiptModal));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateReceiptHTML = (r) => `
    <html><head><title>Payment Receipt</title>
    <style>
      body { font-family: 'Georgia', serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #111; }
      .header { border-bottom: 3px solid #00a99d; padding-bottom: 16px; margin-bottom: 24px; }
      .header h1 { font-size: 24px; margin: 0; color: #1a1a1a; }
      .header p { margin: 4px 0 0; color: #666; font-size: 14px; }
      .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
      .info-block label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #999; margin-bottom: 4px; }
      .info-block p { margin: 0; font-size: 14px; }
      .amount-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
      .amount-table th { text-align: left; padding: 8px; border-bottom: 1px solid #ddd; font-size: 12px; text-transform: uppercase; color: #999; }
      .amount-table td { padding: 8px; border-bottom: 1px solid #eee; font-size: 14px; }
      .amount-table .total td { border-top: 2px solid #00a99d; font-weight: bold; font-size: 16px; }
      .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #eee; font-size: 12px; color: #999; text-align: center; }
    </style></head><body>
    <div class="header">
      <h1>Payment Receipt</h1>
      <p>${r.organisation_name || 'Realty JAG'}</p>
    </div>
    <div class="info-grid">
      <div class="info-block"><label>Tenant</label><p>${r.tenant_name}</p></div>
      <div class="info-block"><label>Receipt #</label><p>${r.payment_id.slice(0, 8).toUpperCase()}</p></div>
      <div class="info-block"><label>Property</label><p>${r.property_name}, Unit ${r.unit_number}</p></div>
      <div class="info-block"><label>Payment Date</label><p>${r.paid_date || r.due_date}</p></div>
    </div>
    <table class="amount-table">
      <thead><tr><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
      <tbody>
        <tr><td>${r.description}</td><td style="text-align:right">$${r.amount.toFixed(2)}</td></tr>
        ${r.late_fee > 0 ? `<tr><td>Late Fee</td><td style="text-align:right">$${r.late_fee.toFixed(2)}</td></tr>` : ''}
        <tr class="total"><td>Total Paid</td><td style="text-align:right">$${r.total.toFixed(2)}</td></tr>
      </tbody>
    </table>
    <div class="footer">
      <p>Status: ${r.status} | Due Date: ${r.due_date}</p>
      <p>This is an automatically generated receipt. Thank you for your payment.</p>
    </div>
    </body></html>`;

  if (loading) {
    return (
      <DashboardLayout type="tenant" title={t('tenant.documents')}>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-card animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="tenant" title={t('tenant.documents')}>
      <div className="space-y-8" data-testid="tenant-documents">
        <div>
          <h2 className="font-serif text-2xl mb-1">My Documents</h2>
          <p className="text-muted-foreground">Access your lease documents and payment receipts</p>
        </div>

        {/* Lease Documents */}
        <div>
          <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gold" />
            Lease Documents
          </h3>
          {leaseDocs.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No lease documents uploaded yet</p>
                <p className="text-sm text-muted-foreground mt-1">Your landlord will upload lease documents here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {leaseDocs.map((doc) => (
                <Card key={doc.id} className="bg-card border-border hover:border-gold/30 transition-colors" data-testid={`lease-doc-${doc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{doc.filename}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {doc.property_name && `${doc.property_name} · `}
                            {doc.unit_number && `Unit ${doc.unit_number} · `}
                            {new Date(doc.uploaded_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleDownloadDoc(doc)} data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Payment Receipts */}
        <div>
          <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-400" />
            Payment Receipts
          </h3>
          {receipts.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Receipt className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No payment receipts available</p>
                <p className="text-sm text-muted-foreground mt-1">Receipts will appear here after you make payments</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {receipts.map((receipt) => (
                <Card key={receipt.payment_id} className="bg-card border-border hover:border-gold/30 transition-colors" data-testid={`receipt-${receipt.payment_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center shrink-0">
                          <DollarSign className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium">{receipt.description}</p>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
                            {receipt.unit_number && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3.5 h-3.5" />
                                Unit {receipt.unit_number}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              Paid: {receipt.paid_date}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-semibold text-emerald-400">${receipt.amount.toFixed(2)}</p>
                          {receipt.late_fee > 0 && (
                            <p className="text-xs text-red-400">+${receipt.late_fee.toFixed(2)} late fee</p>
                          )}
                        </div>
                        <Button size="sm" variant="outline" className="border-gold/30 text-gold hover:bg-gold/10" onClick={() => handleViewReceipt(receipt)} data-testid={`view-receipt-${receipt.payment_id}`}>
                          View
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Monthly Statements */}
        <div>
          <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-blue-400" />
            Monthly Statements
          </h3>
          <p className="text-sm text-muted-foreground mb-4">Download PDF statements for previous months (includes payments, late fees, and balance)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {getStatementMonths().map((m) => (
              <Card key={m.value} className="bg-card border-border hover:border-gold/30 transition-colors" data-testid={`statement-${m.value}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-500/10 flex items-center justify-center shrink-0">
                      <ScrollText className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="text-sm font-medium">{m.label}</span>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    className="text-muted-foreground hover:text-gold"
                    onClick={() => handleDownloadStatement(m.value)}
                    disabled={statementLoading === m.value}
                    data-testid={`download-statement-${m.value}`}
                  >
                    {statementLoading === m.value ? (
                      <span className="text-xs">Loading...</span>
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Receipt Modal */}
        <Dialog open={!!receiptModal} onOpenChange={() => setReceiptModal(null)}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <Receipt className="w-5 h-5 text-gold" />
                Payment Receipt
              </DialogTitle>
            </DialogHeader>
            {receiptModal && (
              <div className="space-y-4" data-testid="receipt-modal">
                <div className="border-b border-border pb-4">
                  <p className="text-sm text-muted-foreground">{receiptModal.organisation_name}</p>
                  <p className="text-xs text-muted-foreground">Receipt #{receiptModal.payment_id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Tenant</p>
                    <p>{receiptModal.tenant_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Property</p>
                    <p>{receiptModal.property_name}, Unit {receiptModal.unit_number}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Due Date</p>
                    <p>{receiptModal.due_date}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Paid Date</p>
                    <p>{receiptModal.paid_date || '—'}</p>
                  </div>
                </div>
                <div className="bg-white p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{receiptModal.description}</span>
                    <span>${receiptModal.amount?.toFixed(2)}</span>
                  </div>
                  {receiptModal.late_fee > 0 && (
                    <div className="flex justify-between text-sm text-red-400">
                      <span>Late Fee</span>
                      <span>${receiptModal.late_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-gold pt-2 border-t border-border">
                    <span>Total</span>
                    <span>${receiptModal.total?.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setReceiptModal(null)} data-testid="receipt-close-btn">Close</Button>
                  <Button size="sm" className="bg-gold text-white hover:bg-gold-light" onClick={handlePrintReceipt} data-testid="receipt-print-btn">
                    <Download className="w-4 h-4 mr-1" />
                    Print / Save as PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TenantDocuments;
